import * as HighCharts from 'highcharts';
import { numberFormat } from 'highcharts';
import AccessibilityModule from 'highcharts/modules/accessibility';
import { ChartType, Colors, Styles } from '../models/shared-charts.model';
import { defaultAxisLabelStyles, defaultPlotOptionConfigs, defaultSeriesItems, defaultTooltipConfigs, defaultXAxisConfigs, defaultYAxisConfigs, } from './column-chart-default-configs';
import { BORDER_PADDING, INSIDE_PADDING, SPACE_BETWEEN_NUMBERS, } from './column-chart.interface';
import * as i0 from "@angular/core";
import * as i1 from "highcharts-angular";
export class ColumnChartComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.Highcharts = HighCharts;
        this.chartOption = {};
        this.chartCallback = (chart) => {
            this.chart = chart;
        };
        this.data = [];
        this.axisLabelStyles = defaultAxisLabelStyles;
        this.xAxisConfigs = defaultXAxisConfigs;
        this.yAxisConfigs = defaultYAxisConfigs;
        this.tooltipItems = [];
        this.plotOptionConfigs = defaultPlotOptionConfigs;
        this.seriesItems = defaultSeriesItems;
        this.chartItems = {
            height: '240',
            width: null,
            type: ChartType.COLUMN,
            drawSVGArrow: true,
            margin: [30, 0, 42, 45],
            spacing: [0, 0, 0, 0],
            animation: true,
            enableDynamicDataLabelPlacement: true,
            isDataLabelCalculationNeeded: false,
        };
        this.tooltipConfigs = defaultTooltipConfigs;
        this.zeroState = '';
        this.barWidthInPercent = null;
        this.options = {};
        this.width = '100%';
        this.chartWidth = 0;
        this.parentContainerWidth = 0;
        this.setDatalabelsOutside = false;
        this.initialSeriesItems = [];
    }
    ngOnInit() {
        if (this.chartItems.isDataLabelCalculationNeeded) {
            this.initialSeriesItems = JSON.parse(JSON.stringify(this.seriesItems));
        }
        AccessibilityModule(HighCharts);
        this.initializeChart();
    }
    ngOnChanges(changes) {
        if (this.data && changes['data']) {
            this.setChartOptions();
            this.chart && this.chart.update(this.options, true, true);
        }
    }
    initializeChart() {
        var _a, _b;
        if (this.chartItems.isRelativeBarWidth) {
            this.setBarWidth();
        }
        this.options = {
            chart: {
                type: this.chartItems.type,
                height: this.chartItems.height,
                spacing: this.chartItems.spacing,
                margin: this.chartItems.margin,
                reflow: true,
                animation: (_a = this.chartItems.animation) !== null && _a !== void 0 ? _a : true,
                plotBorderColor: this.chartItems.plotBorderColor,
                plotBorderWidth: this.chartItems.plotBorderWidth,
            },
            accessibility: {
                enabled: true,
                keyboardNavigation: {
                    enabled: true,
                    focusBorder: {
                        enabled: true,
                        hideBrowserFocusOutline: true,
                        margin: 2,
                    },
                },
                point: {
                    descriptionFormatter: (point) => {
                        var _a;
                        const idx = this.data.findIndex((item) => item.label === point.category);
                        return (_a = this.tooltipItems[idx]) === null || _a === void 0 ? void 0 : _a.element;
                    },
                },
            },
            title: {
                text: '',
            },
            yAxis: {
                title: {
                    text: '',
                },
            },
            legend: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            tooltip: {
                followPointer: true,
                useHTML: true,
                backgroundColor: Colors.WHITE,
                borderWidth: 0,
                padding: 0,
                borderColor: Colors.TRANSPARENT,
                shadow: false,
            },
            plotOptions: {},
            series: [],
        };
        this.setChartOptions();
        requestAnimationFrame(() => {
            this.chart && this.chart.reflow();
            this.chartItems.isDataLabelCalculationNeeded && this.redraw();
        });
        if (this.chartItems.isRelativeBarWidth) {
            this.options.chart = Object.assign(Object.assign({}, this.options.chart), { marginRight: ((_b = this.parentContainerWidth) !== null && _b !== void 0 ? _b : 0) - this.chartWidth });
        }
        else {
            this.options.chart = Object.assign(Object.assign({}, this.options.chart), { width: this.chartItems.width });
        }
    }
    updateDataLabelPlacement() {
        if (this.chartItems.enableDynamicDataLabelPlacement) {
            this.options.chart = Object.assign(Object.assign({}, this.options.chart), { events: {
                    load: function () {
                        const points = this.series[1].points;
                        if (points.length) {
                            points.forEach((point) => {
                                const shapeArgs = point.shapeArgs;
                                if (shapeArgs && shapeArgs['height'] < 30) {
                                    point.update({ dataLabels: { inside: false } }, false);
                                }
                            });
                        }
                    },
                } });
        }
    }
    setXAxisDetails() {
        var _a, _b;
        this.options.xAxis = Object.assign(Object.assign({}, this.options.xAxis), { visible: (_b = (_a = this.xAxisConfigs) === null || _a === void 0 ? void 0 : _a.visible) !== null && _b !== void 0 ? _b : true, categories: this.data.map((category) => category.label), lineWidth: this.xAxisConfigs.lineWidth, lineColor: this.xAxisConfigs.lineColor, labels: {
                enabled: this.xAxisConfigs.labelsEnabled,
                rotation: this.xAxisConfigs.labelRotation,
                style: {
                    fontSize: this.axisLabelStyles.fontSize,
                    fontFamily: this.axisLabelStyles.fontFamily,
                    color: this.axisLabelStyles.color,
                },
            } });
    }
    setYAxisDetails() {
        var _a, _b;
        this.options.yAxis = Object.assign(Object.assign({}, this.options.yAxis), { visible: (_b = (_a = this.yAxisConfigs) === null || _a === void 0 ? void 0 : _a.visible) !== null && _b !== void 0 ? _b : true, lineWidth: this.yAxisConfigs.lineWidth, lineColor: this.yAxisConfigs.lineColor, tickAmount: this.yAxisConfigs.tickAmount, tickColor: this.yAxisConfigs.tickColor, min: this.yAxisConfigs.min, max: this.yAxisConfigs.max, allowDecimals: false, tickPositions: this.yAxisConfigs.tickPositions, stackLabels: {
                enabled: this.yAxisConfigs.stackLabels,
            }, labels: {
                enabled: this.yAxisConfigs.labelsEnabled,
                formatter: function () {
                    return numberFormat(+this.value, 0, '', ',');
                },
                style: {
                    fontSize: this.axisLabelStyles.fontSize,
                    fontFamily: this.axisLabelStyles.fontFamily,
                    color: this.axisLabelStyles.color,
                },
            } });
    }
    setChartPlotOptions() {
        var _a, _b, _c, _d;
        const that = this;
        const chartType = this.chartItems.type;
        this.options.plotOptions = Object.assign(Object.assign({}, this.options.plotOptions), { [chartType]: {
                cursor: (_a = this.plotOptionConfigs) === null || _a === void 0 ? void 0 : _a.cursor,
                maxPointWidth: this.plotOptionConfigs.maxPointWidth,
                borderWidth: this.plotOptionConfigs.borderWidth,
                pointPadding: (_b = this.plotOptionConfigs.pointPadding) !== null && _b !== void 0 ? _b : 0.1,
                groupPadding: (_c = this.plotOptionConfigs.groupPadding) !== null && _c !== void 0 ? _c : 0.2,
                stacking: this.plotOptionConfigs.stacking,
                animation: this.chartItems.animation,
                allowPointSelect: (_d = this.plotOptionConfigs.isClickable) !== null && _d !== void 0 ? _d : false,
                states: {
                    hover: {
                        enabled: this.plotOptionConfigs.hover,
                        borderColor: Colors.WHITE,
                        borderWidth: 2,
                    },
                    inactive: { opacity: this.plotOptionConfigs.inactiveOpacity },
                },
                point: {
                    events: {
                        click: this.plotOptionConfigs.onClick,
                        mouseOver: function () {
                            that.plotOptionConfigs.addCustomBarBorder &&
                                that.plotOptionConfigs.hasHoverState &&
                                setTimeout(() => {
                                    that.drawPointHoverState(this);
                                });
                        },
                        mouseOut: function () {
                            that.plotOptionConfigs.addCustomBarBorder &&
                                that.plotOptionConfigs.hasHoverState &&
                                setTimeout(() => {
                                    that.removePointHoverState(this);
                                });
                        },
                    },
                },
            } });
    }
    drawPointHoverState(point) {
        const chartWidth = this.chartWidth;
        const { y, width, height } = point.shapeArgs;
        const x1 = chartWidth - (y + height + 1);
        const height1 = width;
        const width1 = height;
        this.chart.customPointHoverState = this.chart.renderer
            .rect({
            x: x1,
            y: 0,
            width: width1,
            height: height1,
            zIndex: 100,
            fill: 'none',
            class: 'column-chart-hover-state',
        })
            .add();
        // TODO: Think of a more dynamic way to update point style.
        point.update({
            dataLabels: {
                style: {
                    fontFamily: Styles.OPEN_SANS_BOLD,
                },
            },
        });
    }
    removePointHoverState(point) {
        // TODO: Think of a more dynamic way to handle style update.
        if (point && typeof point.update === 'function') {
            point === null || point === void 0 ? void 0 : point.update({
                dataLabels: {
                    style: {
                        fontFamily: Styles.OPEN_SANS_SEMIBOLD,
                    },
                },
            });
            this.chart.customPointHoverState.destroy();
        }
    }
    tooltipPositioner(labelHeight, point, labelWidth) {
        const { plotLeft, plotTop, chartWidth } = this.chart;
        let x = point.plotX + plotLeft;
        const y = point.plotY + plotTop - labelHeight;
        if (chartWidth < x + labelWidth) {
            x = Math.max(x - labelWidth, 0);
        }
        return { x, y };
    }
    setTooltipConfigs() {
        var _a;
        const that = this;
        this.options.tooltip = Object.assign(Object.assign({}, this.options.tooltip), { shared: this.tooltipConfigs.sharedTooltip, enabled: that.tooltipConfigs.showTooltip, hideDelay: (_a = that.tooltipConfigs.tooltipDelay) !== null && _a !== void 0 ? _a : 0.3, outside: true, style: {
                width: that.tooltipConfigs.tooltipWidth,
            }, positioner: function (_, labelHeight, point) {
                return that.tooltipPositioner(labelHeight, point, this.getLabel().getBBox().width);
            }, formatter: function () {
                const idx = that.data.findIndex((item) => item.label === this.x);
                return that.tooltipItems[idx].element;
            } });
    }
    setBarChartSeries() {
        this.options.series = [];
        // TODO: SB: Make this feature configurable from consumer.
        let offSets;
        if (this.chartItems.isDynamicDatalabelPositioning) {
            offSets = this.getDatalabelOffsets();
        }
        if (this.chartItems.isDataLabelCalculationNeeded) {
            this.setDatalabel();
        }
        this.options.series = this.seriesItems.map((item) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            let series = {
                id: item === null || item === void 0 ? void 0 : item.id,
                name: item.name,
                data: item.data,
                color: item.color,
                minPointLength: item.minPointLength || 0,
                states: {
                    hover: {
                        color: item.hoverColor,
                    },
                    select: {
                        color: item.color,
                    },
                },
                dataLabels: {
                    enabled: item.dataLabel ? item.dataLabel.enabled : false,
                    crop: false,
                    allowOverlap: true,
                    overflow: 'allow',
                    align: (_a = item.dataLabel) === null || _a === void 0 ? void 0 : _a.align,
                    formatter: function () {
                        return numberFormat(this.y, 0, '', ',');
                    },
                    format: (_b = item.dataLabel) === null || _b === void 0 ? void 0 : _b.format,
                    inside: (_c = item.dataLabel) === null || _c === void 0 ? void 0 : _c.inside,
                    y: (_d = item.dataLabel) === null || _d === void 0 ? void 0 : _d.y,
                    x: (_e = item.dataLabel) === null || _e === void 0 ? void 0 : _e.x,
                    padding: (_g = (_f = item.dataLabel) === null || _f === void 0 ? void 0 : _f.padding) !== null && _g !== void 0 ? _g : 10,
                    style: {
                        textOutline: 'none',
                        color: (_h = item.dataLabel) === null || _h === void 0 ? void 0 : _h.color,
                        fontFamily: (_j = item.dataLabel) === null || _j === void 0 ? void 0 : _j.fontFamily,
                        fontSize: (_k = item.dataLabel) === null || _k === void 0 ? void 0 : _k.fontSize,
                    },
                },
            };
            if (this.chartItems.isDynamicDatalabelPositioning) {
                series = Object.assign(Object.assign({}, series), { dataLabels: Object.assign(Object.assign({}, series.dataLabels), { x: (_l = offSets.get(item.uniqueId)) === null || _l === void 0 ? void 0 : _l.offSet }) });
            }
            if (this.setDatalabelsOutside) {
                const maxElement = this.seriesItems.reduce((total, item) => total + item.data[0], 0);
                const dataPoint = this.seriesItems[0].data[0] / maxElement;
                const dataPixel = dataPoint * this.chartWidth;
                series = Object.assign(Object.assign({}, series), { dataLabels: Object.assign(Object.assign({}, series.dataLabels), { x: dataPixel }) });
            }
            //////
            return series;
        });
    }
    redraw() {
        var _a;
        if (this.chart) {
            if (this.chartItems.isRelativeBarWidth) {
                this.setBarWidth();
                if (this.chartItems.isDataLabelCalculationNeeded) {
                    this.seriesItems = JSON.parse(JSON.stringify(this.initialSeriesItems));
                    this.setDatalabelsOutside = false;
                    this.setBarChartSeries();
                    this.chart.update(this.options, false, false);
                }
                this.chart.update({
                    chart: {
                        marginRight: ((_a = this.parentContainerWidth) !== null && _a !== void 0 ? _a : 0) - this.chartWidth,
                    },
                });
            }
            this.chart.reflow();
            if (this.chartItems.isDataLabelCalculationNeeded) {
                this.chart.redraw();
            }
            const width = this.chart.plotWidth;
            if (width > 0) {
                this.plotOptionConfigs.addCustomBarBorder &&
                    this.chart.customBarBorder.attr({ width: width - 1 });
            }
            this.zeroState && this.chart.customText.attr({ x: width / 2 }); // update zeroState message position.
        }
    }
    drawBarBorder() {
        var _a;
        if (this.plotOptionConfigs.addCustomBarBorder) {
            const that = this;
            this.options.chart = Object.assign(Object.assign({}, this.options.chart), { events: Object.assign(Object.assign({}, (_a = this.options.chart) === null || _a === void 0 ? void 0 : _a.events), { load() {
                        var _a;
                        const chart = this;
                        const { plotLeft: x, plotWidth: width, series } = chart;
                        const y = (_a = series[0].points[0].plotY) !== null && _a !== void 0 ? _a : 0;
                        chart.customBarBorder = chart.renderer
                            .rect({
                            x: x + 0.5,
                            y: y + 0.5,
                            width: width - 1,
                            height: that.plotOptionConfigs.maxPointWidth,
                            fill: 'none',
                            stroke: Colors.GRAY_DUSTY,
                            'stroke-width': 1,
                            zIndex: 3,
                        })
                            .add();
                    } }) });
        }
    }
    drawZeroStateMessage() {
        var _a;
        if (this.zeroState) {
            const that = this;
            this.options.chart = Object.assign(Object.assign({}, this.options.chart), { events: Object.assign(Object.assign({}, (_a = this.options.chart) === null || _a === void 0 ? void 0 : _a.events), { load() {
                        const chart = this, fixedOffset = 30;
                        chart.customText = chart.renderer
                            .text(that.zeroState, chart.plotWidth / 2, fixedOffset + chart.plotHeight / 2)
                            .attr({ zIndex: 1 })
                            .css({
                            fontSize: Styles.FONT_SIZE_16,
                            color: Colors.GRAY_DARKER,
                            fontFamily: Styles.POPPINS_MEDIUM,
                        })
                            .add();
                    } }) });
        }
    }
    drawSVGPath() {
        var _a;
        if (this.chartItems.drawSVGArrow) {
            this.options.chart = Object.assign(Object.assign({}, this.options.chart), { events: Object.assign(Object.assign({}, (_a = this.options.chart) === null || _a === void 0 ? void 0 : _a.events), { render() {
                        const chart = this;
                        const plotAreaHeight = chart.plotHeight, fixedOffset = 20, upperArrowOffset = 4, lowerArrowOffset = 5, upArrowPoint1 = plotAreaHeight + fixedOffset + upperArrowOffset, upArrowPoint2 = plotAreaHeight + fixedOffset, upArrowPoint3 = plotAreaHeight + fixedOffset, upArrowPoint4 = plotAreaHeight + fixedOffset + lowerArrowOffset;
                        const upArrow = [
                            ['M', 0, 0],
                            ['L', upArrowPoint1, 0],
                            ['L', upArrowPoint2, 3],
                            ['L', upArrowPoint3, -3],
                            ['L', upArrowPoint4, 0],
                        ];
                        chart.renderer
                            .path(upArrow)
                            .attr({
                            'stroke-width': 1,
                            stroke: Colors.BLACK,
                            fill: Colors.BLACK,
                            rotation: -90,
                            zIndex: 4,
                        })
                            .translate(chart.plotLeft, chart.plotHeight + chart.plotTop + 1)
                            // To set the starting point of the arrow mark at the origin of chart dynamically
                            .add();
                    } }) });
        }
    }
    getDatalabelOffsets() {
        let countOfRightAlignedBars = 0;
        this.seriesItems.map((item) => {
            var _a;
            if (((_a = item.dataLabel) === null || _a === void 0 ? void 0 : _a.recommededAlignment) === 'left') {
                countOfRightAlignedBars += 1;
            }
        });
        const areSubBarsAlignedRight = countOfRightAlignedBars >= 2;
        if (areSubBarsAlignedRight) {
            return this.getDatalabelOffsetsAlignment('left');
        }
        else {
            return this.getDatalabelOffsetsAlignment('right');
        }
    }
    getDatalabelOffsetsAlignment(align) {
        let overflow = 0;
        const maxElement = this.seriesItems.reduce((total, item) => total + item.data[0], 0);
        let seriesItems = [];
        if (align === 'left') {
            seriesItems = [...this.seriesItems];
        }
        else {
            seriesItems = [...this.seriesItems].reverse();
        }
        const offSets = new Map();
        seriesItems.forEach((item) => {
            var _a, _b;
            const dataPoint = item.data[0] / maxElement;
            const dataPixel = dataPoint * this.chartWidth;
            // TODO: SB: Label pixel will vary by resolution, can't hard code to 7
            const labelPixel = ((_a = item.data[0]) === null || _a === void 0 ? void 0 : _a.toString().length) * 7;
            let offSet = 0;
            const spaceToLeft = dataPixel / 2 - overflow;
            if (spaceToLeft < labelPixel / 2) {
                offSet = labelPixel / 2 - spaceToLeft;
                overflow = labelPixel / 2 + 2 + offSet;
            }
            offSets.set(item.uniqueId, {
                offSet,
                recommededAlignment: (_b = item.dataLabel) === null || _b === void 0 ? void 0 : _b.recommededAlignment,
            });
        });
        for (const [id, value] of offSets) {
            if (value.recommededAlignment === 'left') {
                value.offSet = -value.offSet;
            }
        }
        return offSets;
    }
    checkIfFits(dataPixel, labelPixel) {
        return labelPixel <= dataPixel;
    }
    checkIfBothDataLabelsFit(dataLabelCannotFit, dataLabelCanFit) {
        var _a, _b, _c;
        const barPixelLength = (_a = dataLabelCanFit === null || dataLabelCanFit === void 0 ? void 0 : dataLabelCanFit.dataPixel) !== null && _a !== void 0 ? _a : 0;
        const dataLabelPixel = ((_b = dataLabelCannotFit === null || dataLabelCannotFit === void 0 ? void 0 : dataLabelCannotFit.labelPixel) !== null && _b !== void 0 ? _b : 0) +
            ((_c = dataLabelCanFit === null || dataLabelCanFit === void 0 ? void 0 : dataLabelCanFit.labelPixel) !== null && _c !== void 0 ? _c : 0) +
            INSIDE_PADDING +
            BORDER_PADDING +
            SPACE_BETWEEN_NUMBERS;
        return this.checkIfFits(barPixelLength, dataLabelPixel);
    }
    setBothDatalabelsOutside() {
        this.setDatalabelsOutside = true;
        this.seriesItems[1].dataLabel = Object.assign(Object.assign({}, this.seriesItems[1].dataLabel), { inside: false, enabled: true, format: `${this.seriesItems[1].data[0]}|${this.seriesItems[0].data[0]}` });
        this.seriesItems[0].dataLabel = Object.assign(Object.assign({}, this.seriesItems[0].dataLabel), { enabled: false });
    }
    setDataLabelInside(dataLabelCannotFit) {
        const dataLabelToMove = this.seriesItems.find((item) => item.name === (dataLabelCannotFit === null || dataLabelCannotFit === void 0 ? void 0 : dataLabelCannotFit.name));
        if (dataLabelToMove && dataLabelToMove.dataLabel) {
            dataLabelToMove.dataLabel = Object.assign(Object.assign({}, dataLabelToMove.dataLabel), { inside: false });
        }
    }
    setSubmittedLabelOutside(dataLabelCannotFit, dataLabelCanFit) {
        var _a, _b, _c;
        const dataLabelToRemove = this.seriesItems.find((item) => item.name === (dataLabelCannotFit === null || dataLabelCannotFit === void 0 ? void 0 : dataLabelCannotFit.name));
        const PADDING_FOR_SUBMITTED_LABEL_OUTSIDE = 2;
        const barPixelLength = ((_a = dataLabelCanFit === null || dataLabelCanFit === void 0 ? void 0 : dataLabelCanFit.dataPixel) !== null && _a !== void 0 ? _a : 0) + ((_b = dataLabelCannotFit === null || dataLabelCannotFit === void 0 ? void 0 : dataLabelCannotFit.dataPixel) !== null && _b !== void 0 ? _b : 0);
        const dataLabelPixel = (_c = dataLabelCannotFit === null || dataLabelCannotFit === void 0 ? void 0 : dataLabelCannotFit.labelPixel) !== null && _c !== void 0 ? _c : 0;
        const moveOutside = this.checkIfFits(this.parentContainerWidth, barPixelLength + dataLabelPixel + PADDING_FOR_SUBMITTED_LABEL_OUTSIDE);
        if (moveOutside) {
            if (dataLabelToRemove && dataLabelToRemove.dataLabel) {
                dataLabelToRemove.dataLabel = Object.assign(Object.assign({}, dataLabelToRemove.dataLabel), { enabled: true, inside: false, align: 'left', x: 0, padding: PADDING_FOR_SUBMITTED_LABEL_OUTSIDE });
            }
        }
        else {
            if (dataLabelToRemove && dataLabelToRemove.dataLabel) {
                dataLabelToRemove.dataLabel = Object.assign(Object.assign({}, dataLabelToRemove.dataLabel), { enabled: true, align: 'left', x: -1 * (dataLabelPixel + INSIDE_PADDING), padding: 0 });
            }
        }
    }
    centerDataLabel(infoForLabel) {
        const dataLabelToMove = this.seriesItems.find((item) => item.name === (infoForLabel === null || infoForLabel === void 0 ? void 0 : infoForLabel.name));
        if (dataLabelToMove && dataLabelToMove.dataLabel) {
            dataLabelToMove.dataLabel = Object.assign(Object.assign({}, dataLabelToMove.dataLabel), { align: 'center', padding: 0 });
        }
    }
    setDatalabel() {
        const maxElement = this.seriesItems.reduce((total, item) => total + item.data[0], 0);
        const pixelData = [];
        this.seriesItems.map((item) => {
            var _a, _b;
            if ((_a = item.dataLabel) === null || _a === void 0 ? void 0 : _a.enabled) {
                const dataPoint = item.data[0] / maxElement;
                const dataPixel = dataPoint * this.chartWidth;
                const labelPixel = ((_b = item.data[0]) === null || _b === void 0 ? void 0 : _b.toString().length) * 7;
                const canFitInBar = this.checkIfFits(dataPixel, labelPixel + BORDER_PADDING + SPACE_BETWEEN_NUMBERS);
                pixelData.push({
                    canFit: canFitInBar,
                    name: item.name,
                    dataPixel,
                    labelPixel,
                });
            }
        });
        if (pixelData.length === 1) {
            //check if the right side padding is more than set padding on left
            const infoForLabel = pixelData[0];
            const labelLength = infoForLabel.labelPixel + BORDER_PADDING;
            const leftoverChart = infoForLabel.dataPixel - labelLength;
            if (leftoverChart < BORDER_PADDING) {
                this.centerDataLabel(infoForLabel);
            }
        }
        else if (pixelData.length > 1) {
            const allDataLabelsCanFit = pixelData.every((item) => item.canFit);
            const noDataLabelsCanFit = pixelData.every((item) => !item.canFit);
            if (allDataLabelsCanFit) {
                // Do nothing
            }
            else if (noDataLabelsCanFit) {
                this.setBothDatalabelsOutside();
            }
            else {
                const dataLabelCannotFit = pixelData.find((item) => !item.canFit);
                const dataLabelCanFit = pixelData.find((item) => item.canFit);
                if ((dataLabelCannotFit === null || dataLabelCannotFit === void 0 ? void 0 : dataLabelCannotFit.name) === 'Submitted') {
                    this.setSubmittedLabelOutside(dataLabelCannotFit, dataLabelCanFit);
                }
                else {
                    const fitsInside = this.checkIfBothDataLabelsFit(dataLabelCannotFit, dataLabelCanFit);
                    if (fitsInside) {
                        this.setDataLabelInside(dataLabelCannotFit);
                    }
                    else {
                        this.setBothDatalabelsOutside();
                    }
                }
            }
        }
    }
    setBarWidth() {
        var _a;
        const MIN_CHART_LENGTH = 17; //2px is taken off when drawing TODO VM
        // Fetch container component within the current instance of column chart component
        const hostElement = this.elementRef.nativeElement;
        const chartWrapper = hostElement.querySelector('.realize-reports-column-chart-wrapper');
        this.parentContainerWidth = (chartWrapper === null || chartWrapper === void 0 ? void 0 : chartWrapper.offsetWidth) || 0;
        this.chartWidth = Math.max(Number(this.barWidthInPercent) * ((_a = this.parentContainerWidth) !== null && _a !== void 0 ? _a : 0), MIN_CHART_LENGTH);
    }
    onResize() {
        this.redraw();
    }
    setChartOptions() {
        this.setXAxisDetails();
        this.setYAxisDetails();
        this.setBarChartSeries();
        this.setChartPlotOptions();
        this.setTooltipConfigs();
        this.updateDataLabelPlacement();
        this.drawSVGPath();
        this.drawZeroStateMessage();
        this.drawBarBorder();
    }
}
ColumnChartComponent.ɵfac = function ColumnChartComponent_Factory(t) { return new (t || ColumnChartComponent)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
ColumnChartComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ColumnChartComponent, selectors: [["realize-reports-column-chart"]], hostBindings: function ColumnChartComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("resize", function ColumnChartComponent_resize_HostBindingHandler($event) { return ctx.onResize($event); }, false, i0.ɵɵresolveWindow);
    } }, inputs: { data: "data", axisLabelStyles: "axisLabelStyles", xAxisConfigs: "xAxisConfigs", yAxisConfigs: "yAxisConfigs", tooltipItems: "tooltipItems", plotOptionConfigs: "plotOptionConfigs", seriesItems: "seriesItems", chartItems: "chartItems", tooltipConfigs: "tooltipConfigs", zeroState: "zeroState", barWidthInPercent: "barWidthInPercent" }, features: [i0.ɵɵNgOnChangesFeature], decls: 2, vars: 3, consts: [["id", "column-chart", 1, "realize-reports-column-chart-wrapper"], [1, "realize-reports-column-chart", 3, "callbackFunction", "Highcharts", "options"]], template: function ColumnChartComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "highcharts-chart", 1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("callbackFunction", ctx.chartCallback)("Highcharts", ctx.Highcharts)("options", ctx.options);
    } }, directives: [i1.HighchartsChartComponent], styles: ["/* fonts */\n/* text sizes */\n/* 1.875rem (30px) */\n/* 1.375rem (22px)*/\n/* 1.25rem (20px) */\n/* 1.125rem (18px) */\n/* 1rem (16px) */\n/* 0.875rem (14px) */\n/* 0.75rem (12px) */\n/* fonts */\n/* text sizes */\n/* 1.875rem (30px) */\n/* 1.375rem (22px)*/\n/* 1.25rem (20px) */\n/* 1.125rem (18px) */\n/* 1rem (16px) */\n/* 0.875rem (14px) */\n/* 0.75rem (12px) */\n.lst-table-responsive-wrapper {\n  width: var(--lst-table-responsive-wrapper-width, auto);\n  height: var(--lst-table-responsive-wrapper-height, auto);\n  overflow: var(--lst-table-responsive-wrapper-overflow, scroll);\n}\n.lst-table {\n  border-collapse: var(--lst-table-border-collapse, collapse);\n  border: var(--lst-table-border, none);\n  background-color: #fff;\n  font-family: var(--lst-table-font-family, \"OpenSans\");\n  line-height: var(--lst-table-line-height, 20px);\n  font-size: var(--lst-table-font-size, 0.875rem);\n}\n.lst-table.compact > thead > tr > th, .lst-table.compact > tbody > tr > td {\n  padding-top: var(--lst-table-padding-top-compact, 8.5px);\n  padding-bottom: var(--lst-table-padding-top-compact, 8.5px);\n}\n.lst-table > thead tr {\n  border-top: var(--lst-table-header-row-border-top, none);\n  background-color: #fff;\n  line-height: var(--lst-table-header-row-line-height, 20px);\n  padding: var(--lst-table-padding, 17px 8px);\n}\n.lst-table > thead > tr > th {\n  border-top: var(--lst-table-th-border-top, none);\n  border-left: var(--lst-table-th-border-left, none);\n  border-right: var(--lst-table-th-border-right, none);\n  border-bottom: var(--lst-table-th-border-bottom, 1px solid #555);\n  font-family: var(--lst-table-th-font-family, \"OpenSans-Semibold\");\n  font-size: var(--lst-table-th-font-size, 0.875rem);\n  line-height: var(--lst-table-th-line-height, 20px);\n  padding: var(--lst-table-th-padding, 8px 4px);\n  min-width: var(--lst-table-th-min-width, 64px);\n  max-width: var(--lst-table-th-max-width, none);\n  text-align: var(--lst-table-th-text-align, left);\n  vertical-align: var(--lst-table-th-vertical-align, middle);\n  white-space: nowrap;\n}\n.lst-table > thead > tr > th > .lst-table-header-icon {\n  vertical-align: var(--lst-table-header-icon-vertical-align, middle);\n  margin-bottom: var(--lst-table-header-icon-margin-bottom, 1px 0);\n}\n.lst-table > thead > tr > th.align-center {\n  text-align: center;\n}\n.lst-table > thead > tr > th.interactive:hover {\n  background-color: var(--lst-table-th-interactive-hover-bg-color, #e5f0f9);\n  border-radius: var(--interactive-hover-th-border-radius, 4px 4px 0 0);\n}\n.lst-table > thead > tr > th.interactive:hover:has(.lst-table-select) {\n  border-radius: var(--interactive-hover-select-th-border-radius, 4px 0 0 0);\n  --primary-select-background-color: #e5f0f9;\n  --primary-styling-label-color: #2150a3;\n  --select-caret-down-color-base: #2150a3;\n}\n.lst-table tbody tr {\n  border-top: var(--lst-table-tr-border-top, none);\n  border-left: var(--lst-table-tr-border-left, none);\n  border-right: var(--lst-table-tr-border-right, none);\n  border-bottom: var(--lst-table-tr-border-bottom, 1px solid #d8d8d8);\n  background-color: #fff;\n}\n.lst-table tbody tr:hover {\n  background-color: var(--lst-tr-hover-bg-color, #e5f0f9);\n}\n.lst-table tbody tr > td {\n  font-size: var(--lst-table-td-font-size, 0.875rem);\n  border-bottom: var(--lst-table-td-border-bottom, none);\n  border-left: var(--lst-table-td-border-left, none);\n  border-right: var(--lst-table-td-border-right, none);\n  padding: var(--lst-table-padding, 17px 8px);\n}\n.lst-table tbody tr > td.align-center {\n  text-align: center;\n}\n.lst-table tbody tr > td.align-left {\n  padding-left: var(--lst-table-td-align-left-padding, none);\n  text-align: left;\n}\n.lst-table .lst-table-select {\n  margin-right: 4px;\n  --single-select-label-font-size: $text-size-small;\n  --single-select-label-font-family: $secondary-font-medium;\n  --label-margin-bottom-borderless: 0;\n}\n.realize-reports-column-chart-wrapper {\n  width: 100%;\n  display: flex;\n  overflow: visible;\n}\n.realize-reports-column-chart {\n  flex-grow: 1;\n  overflow: visible;\n}\n.column-chart-hover-state {\n  stroke-width: 2;\n  stroke: #fff;\n  -moz-filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.66));\n  -ms-filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.66));\n  -o-filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.66));\n  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.66));\n}"], encapsulation: 2 });
